import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { RecruitmentApiService } from '../../services/recruitment-api.service';
import { AdminApiService } from '../../services/admin-api.service';
import {
  BehavioralAlignment,
  CandidateDecision,
  RankedCandidate,
  RecruitmentBenchmark,
} from '../models/recruitment.models';

@Component({
  selector: 'app-benchmark-detail',
  templateUrl: './benchmark-detail.component.html',
  styleUrls: ['./benchmark-detail.component.css'],
  standalone: false,
})
export class BenchmarkDetailComponent implements OnInit {
  benchmark: RecruitmentBenchmark | null = null;
  candidates: RankedCandidate[] = [];
  totalCandidates = 0;
  loading = true;
  errorMessage = '';
  search = '';
  decisionFilter: CandidateDecision | 'ALL' = 'ALL';
  behavioralFilter: BehavioralAlignment | 'ALL' = 'ALL';
  reviewingCandidateId: string | null = null;
  draftDecision: CandidateDecision = 'UNREVIEWED';
  draftNote = '';
  savingDecision = false;

  readonly decisions: CandidateDecision[] = ['UNREVIEWED', 'MOVE_AHEAD', 'HOLD', 'REJECTED'];

  constructor(
    private route: ActivatedRoute,
    private recruitmentApi: RecruitmentApiService,
    private adminApi: AdminApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const benchmarkId = params.get('benchmarkId');
      if (benchmarkId) this.loadPage(benchmarkId);
    });
  }

  loadPage(benchmarkId = this.benchmark?.benchmarkId || ''): void {
    if (!benchmarkId) return;
    this.loading = true;
    this.errorMessage = '';
    forkJoin({
      benchmarks: this.recruitmentApi.getBenchmarks(),
      ranked: this.recruitmentApi.getRankedCandidates(benchmarkId, this.filters()),
    }).subscribe({
      next: ({ benchmarks, ranked }) => {
        this.benchmark = benchmarks.find((item) => item.benchmarkId === benchmarkId) || null;
        this.candidates = ranked.items;
        this.totalCandidates = ranked.total;
        this.loading = false;
        if (!this.benchmark) this.errorMessage = 'This private benchmark was not found.';
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'The candidate ranking could not be loaded.';
      },
    });
  }

  applyFilters(): void {
    if (!this.benchmark) return;
    this.loading = true;
    this.recruitmentApi.getRankedCandidates(this.benchmark.benchmarkId, this.filters()).subscribe({
      next: (response) => {
        this.candidates = response.items;
        this.totalCandidates = response.total;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Unable to update candidate filters', 'Dismiss', { duration: 2800 });
      },
    });
  }

  openReview(candidate: RankedCandidate): void {
    this.reviewingCandidateId = candidate.userId;
    this.draftDecision = candidate.decision;
    this.draftNote = candidate.note || '';
  }

  cancelReview(): void {
    this.reviewingCandidateId = null;
    this.draftNote = '';
  }

  saveReview(candidate: RankedCandidate): void {
    if (!this.benchmark) return;
    this.savingDecision = true;
    this.recruitmentApi.updateCandidateDecision({
      benchmarkId: this.benchmark.benchmarkId,
      candidateId: candidate.userId,
      state: this.draftDecision,
      note: this.draftNote.trim(),
    }).subscribe({
      next: () => {
        candidate.decision = this.draftDecision;
        candidate.note = this.draftNote.trim();
        this.savingDecision = false;
        this.reviewingCandidateId = null;
        this.snackBar.open('Candidate review updated', 'Close', { duration: 2200 });
      },
      error: () => {
        this.savingDecision = false;
        this.snackBar.open('Unable to save candidate review', 'Dismiss', { duration: 2800 });
      },
    });
  }

  viewResume(candidate: RankedCandidate): void {
    this.adminApi.getUserById(candidate.userId).subscribe({
      next: (response) => {
        const resumeUrl = response?.user?.resumeUrl;
        if (resumeUrl) {
          window.open(resumeUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        this.notifyResumeUnavailable();
      },
      error: () => this.notifyResumeUnavailable(),
    });
  }

  private notifyResumeUnavailable(): void {
    this.snackBar.open('Resume is not available on file.', 'Dismiss', { duration: 3200 });
  }

  decisionLabel(decision: CandidateDecision): string {
    return ({ UNREVIEWED: 'Unreviewed', MOVE_AHEAD: 'Move ahead', HOLD: 'Hold', REJECTED: 'Rejected' })[decision];
  }

  initials(candidate: RankedCandidate): string {
    return `${candidate.firstName.charAt(0)}${candidate.lastName.charAt(0)}`.toUpperCase();
  }

  private filters(): Record<string, any> {
    return {
      search: this.search,
      decision: this.decisionFilter,
      behavioral: this.behavioralFilter,
      limit: 25,
    };
  }
}
